import React from 'react';
import { createPortal } from 'react-dom';

import { useTourGuide } from '../TourGuideProvider';
import type { StageProgress, StepProgress } from '../tours';

interface ModuleRow {
  module: StepProgress;
  children: StepProgress[];
}

function toModuleRows(stages: StageProgress[]): ModuleRow[] {
  const rows: ModuleRow[] = [];
  for (const stage of stages) {
    for (const step of stage.steps) {
      if (step.depth === 0) rows.push({ module: step, children: [] });
      else rows[rows.length - 1]?.children.push(step);
    }
  }
  return rows;
}

export function TourTracker() {
  const tour = useTourGuide();
  const listRef = React.useRef<HTMLOListElement>(null);

  const activeStepId = tour?.run.running ? tour.run.stepId : null;

  React.useEffect(() => {
    if (!activeStepId) return;
    const active = listRef.current?.querySelector('[aria-current="step"]');
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeStepId]);

  if (!tour || !tour.run.running) return null;

  const modules = toModuleRows(tour.stageProgress.stages);
  if (modules.length === 0) return null;

  const completedModules = modules.filter((m) => m.module.state === 'complete').length;
  const activeModuleIndex = modules.findIndex((m) => m.module.state === 'active');
  const currentModuleNumber = activeModuleIndex >= 0 ? activeModuleIndex + 1 : completedModules;

  const { index, total } = tour.run;

  return createPortal(
    <aside
      className="tour-guide-tracker"
      data-testid="tour-tracker"
      role="status"
      aria-live="polite"
      aria-label={`Tour progress: module ${currentModuleNumber || 1} of ${modules.length}`}
      onClick={(e) => e.stopPropagation()}
    >
      <header className="tour-guide-tracker__head">
        <span className="tour-guide-tracker__title">Product tour</span>
        <span className="tour-guide-tracker__count" data-testid="tour-tracker-count">
          Module {currentModuleNumber || 1} / {modules.length}
        </span>
        <button
          type="button"
          className="tour-guide-tracker__close"
          onClick={() => tour.stop()}
          aria-label="End tour"
          title="End tour"
          data-testid="tour-tracker-exit"
        >
          ×
        </button>
      </header>

      <ol className="tour-guide-tracker__list" ref={listRef}>
        {modules.map(({ module, children }, i) => (
          <li
            key={module.id}
            className="tour-guide-tracker__item"
            data-state={module.state}
            data-testid={`tour-module-${module.id}`}
          >
            <div className="tour-guide-tracker__steprowwrap">
              <button
                type="button"
                className="tour-guide-tracker__row"
                onClick={() => tour.goToStep(module.id)}
                aria-current={module.state === 'active' ? 'step' : undefined}
              >
                <span className="tour-guide-tracker__marker" aria-hidden="true">
                  {module.state === 'complete' ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <span className="tour-guide-tracker__dot" />
                  )}
                </span>
                <span className="tour-guide-tracker__label">{module.label}</span>
              </button>

              {tour.taskFor(module.id) && (
                <button
                  type="button"
                  className="tour-guide-tracker__steptask"
                  onClick={() => tour.startTask(module.id)}
                  title={`Build one yourself: ${module.label}`}
                  aria-label={`Start the guided walkthrough for ${module.label}`}
                  data-testid={`tour-step-task-${module.id}`}
                >
                  Try
                </button>
              )}
            </div>

            {children.length > 0 && (
              <ol className="tour-guide-tracker__steplist">
                {children.map((step) => (
                  <li
                    key={step.id}
                    className="tour-guide-tracker__step"
                    data-state={step.state}
                    data-depth={step.depth}
                    data-testid={`tour-step-${step.id}`}
                  >
                    <div className="tour-guide-tracker__steprowwrap">
                      <button
                        type="button"
                        className="tour-guide-tracker__steprow"
                        onClick={() => tour.goToStep(step.id)}
                        aria-current={step.state === 'active' ? 'step' : undefined}
                      >
                        <span className="tour-guide-tracker__stepdot" aria-hidden="true" />
                        <span className="tour-guide-tracker__steplabel">{step.label}</span>
                      </button>

                      {tour.taskFor(step.id) && (
                        <button
                          type="button"
                          className="tour-guide-tracker__steptask"
                          onClick={() => tour.startTask(step.id)}
                          title={`Build one yourself: ${step.label}`}
                          aria-label={`Start the guided walkthrough for ${step.label}`}
                          data-testid={`tour-step-task-${step.id}`}
                        >
                          Try
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {i < modules.length - 1 && (
              <span className="tour-guide-tracker__connector" aria-hidden="true" />
            )}
          </li>
        ))}
      </ol>

      <footer className="tour-guide-tracker__foot">
        <span className="tour-guide-tracker__pos" data-testid="tour-tracker-pos">
          Step {index + 1} of {total}
        </span>
      </footer>
    </aside>,
    document.body,
  );
}
